import React from 'react';
import { GovFormWeb } from '@gov-design-system-ce/react-form';
import {
	useRezaListContext,
	useRezaLocale,
	zastupujiciOsobaNazev,
} from '@gov-nx/module/page';
import {
	Button,
	ContentLayout,
	ErrorStatusBanner,
	FilterParameterList,
	FormTabs,
	HeaderSearchBar,
	Icon,
	InfoBanner,
	KeyValues,
	LayoutGap,
	Tile,
	Tiles,
} from '@gov-nx/ui/web';
import { toDateReadable } from '@gov-nx/utils/common';
import { pageRouteResolver } from '@gov-nx/web';
import { MarkdownRender } from '../../../Markdown/MarkdownRender';
import { ComplaintsModal } from '../ComplaintsModal';
import { OverviewModal } from '../OverviewModal';
import { RezaNotAllDisplayedMessage } from '../RezaNotAllDisplayedMessage';
import { UsageModal } from '../UsageModal';
import { FilterModal } from './FilterModal';
import { RezaListSkeleton } from './RezaListSkeleton';

export const RezaList = () => {
	const { t } = useRezaLocale();
	const {
		code,
		isLoading,
		isError,
		data,
		formDefinition,
		onSubmit,
		onFilterReset,
		filterParams,
		isFilterModalOpen,
		isRezaOverviewModalOpen,
		isRezaUsageModalOpen,
		complaintsForm,
	} = useRezaListContext();

	return (
		<LayoutGap>
			<ContentLayout>
				<GovFormWeb
					formDefinition={formDefinition}
					onSubmit={onSubmit}>
					<HeaderSearchBar
						fields={{
							search: formDefinition.fields.text,
							button: formDefinition.fields.button,
						}}
						filterParams={
							<FilterParameterList
								items={filterParams}
								onReset={onFilterReset}
							/>
						}>
						<Button
							onClick={isFilterModalOpen.setTrue}
							color="primary"
							type="outlined"
							size="m"
							aria-label={t('akce.zobrazit-filtr.wcag')}>
							<Icon
								name="funnel"
								type="templates"
							/>
						</Button>
					</HeaderSearchBar>

					<RezaNotAllDisplayedMessage />

					<FormTabs {...formDefinition.fields.role} />

					<FilterModal />
				</GovFormWeb>
				<ComplaintsModal complaintsForm={complaintsForm} />
				<OverviewModal
					isOpen={isRezaOverviewModalOpen.value}
					onClose={isRezaOverviewModalOpen.setFalse}
				/>
				<UsageModal
					isOpen={isRezaUsageModalOpen.value}
					onClose={isRezaUsageModalOpen.setFalse}
				/>

				{isLoading ? (
					<RezaListSkeleton />
				) : isError ? (
					<ErrorStatusBanner code={code} />
				) : data.length === 0 ? (
					<InfoBanner
						headline={t('stav.chyba.zadny-zaznam')}
						text={t('stav.chyba.v-reza-nejsou-data')}
					/>
				) : (
					<Tiles>
						{data.map((row) => (
							<Tile
								key={[
									row.kod,
									row.role,
									row.typ,
									row.stav,
									row.platnostOd,
									zastupujiciOsobaNazev(row),
									row.kodSablony,
								].join('-')}
								name={row.nazevSablony ?? '-'}
								to={pageRouteResolver('reza.detail', { id: row.kod })}
								icon={{ name: 'chevron-right', type: 'components' }}>
								<div className="text-(--text-secondary)!">
									<KeyValues
										options={[
											{
												label: t('seznam.item.platnost-od.label'),
												value: row.platnostOd
													? toDateReadable(row.platnostOd)
													: '-',
											},
											{
												label: t('seznam.item.platnost-do.label'),
												value: row.platnostDo
													? toDateReadable(row.platnostDo)
													: t('seznam.item.platnost-do.empty'),
											},
											{
												label:
													row.role === 'zmocnenec'
														? t('seznam.item.zmocnenec')
														: t('seznam.item.zmocnitel'),
												value: zastupujiciOsobaNazev(row),
											},
											{
												label: t('seznam.item.identifikator'),
												value: row.kod,
											},
										]}
									/>
								</div>
							</Tile>
						))}
					</Tiles>
				)}
			</ContentLayout>

			<MarkdownRender>
				{t('seznam.footer', {
					link: pageRouteResolver('reza.templates'),
				})}
			</MarkdownRender>
			<MarkdownRender>
				{t('seznam.footer-modal-form-description')}
			</MarkdownRender>
			<Button
				color="primary"
				type="link"
				onClick={isRezaOverviewModalOpen.setTrue}>
				{t('overview.title')}
			</Button>
			<Button
				color="primary"
				type="link"
				onClick={isRezaUsageModalOpen.setTrue}>
				{t('usage.title')}
			</Button>
			<Button
				color="primary"
				type="link"
				onClick={complaintsForm.isModalOpen.setTrue}>
				{t('seznam.button.reklamace.label')}
			</Button>
			<MarkdownRender>
				{t('seznam.footer-archive', {
					link: pageRouteResolver('reza.archive'),
				})}
			</MarkdownRender>
		</LayoutGap>
	);
};
